import React, { useEffect, useMemo, useRef, useState } from 'react'

// TODO: eliminate imports from "old" code paths (components/..., lib/...}, move things to V2
// V1 imports
import { Alert, LinearProgress } from '@barracuda-internal/bds-core'
import { useAppSelector } from 'redux/toolkit/hooks'
import { useFormatMessage } from 'lib/localization'
import { PageTitle } from 'components/libs/layout/pages/PageTitle'
import { FormSpacer } from 'components/libs/layout/pages/FormSpacer'
import { SettingsPageContainer } from 'components/libs/layout/pages/SettingsPageContainer'
import appConfig from 'config/appConfig'

// V2 module imports
import { Auth, AuthSubject, EgdApiClient, IAuthPolicy, UrlUtils, useI18n } from 'v2/modules/lib'
import { QuarantineNotificationPage, QuarantineNotificationPageProps } from './QuarantineNotificationPage'
import { QuarantineNotificationForm } from './quarantineNotificationForm'

type UIStatePending = { state: 'pending' }
type UIStateReady = { state: 'ready'; props: QuarantineNotificationPageProps }
type UIStateError = { state: 'error'; error: unknown }

type UIState = UIStatePending | UIStateReady | UIStateError

const BASE_I18N_KEY = 'ess.users.quarantine_notification'

export const QuarantineNotificationPageLoader: React.FC = () => {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)

  const { t } = useI18n()
  const canSetState = useRef(true)
  const [uiState, setUiState] = useState<UIState>({ state: 'pending' })

  // TODO: switch to load dependencies via v2 API
  const { accessToken, accessTokenObject } = useAppSelector(_store => ({
    accessToken: _store.auth.accessToken,
    accessTokenObject: _store.auth.accessTokenObject
  }))

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false
    }
  }, [])

  useEffect(() => {
    if (typeof accessToken === 'undefined' || typeof accessTokenObject === 'undefined') {
      // Should not happen, needed here as type guard on redux values
      setUiState({
        state: 'error',
        error: 'Unauthenticated'
      })
      return
    }

    const { accountId, pdDomainId, authDomainPolicies } = accessTokenObject
    const domainId = pdDomainId ? Number(pdDomainId) : null

    // Check domain-level policy access when in domain mode BEFORE making any API calls
    if (domainId) {
      const authFromRedux = Auth((authDomainPolicies || []) as IAuthPolicy[])
      const canReadDomainPolicies = authFromRedux.checkPolicy({
        subject: AuthSubject.DOMAIN_LEVEL_POLICIES,
        value: { domain_id: domainId },
        action: 'R'
      })
      const canManageDomainPolicies = authFromRedux.checkPolicy({
        subject: AuthSubject.DOMAIN_LEVEL_POLICIES,
        value: { domain_id: domainId },
        action: 'U'
      })
      if (!canReadDomainPolicies && !canManageDomainPolicies) {
        setUiState({
          state: 'error',
          error: 'no_domain_policy_access'
        })
        return
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-extra-semi
    ;(async () => {
      try {
        const urlUtils = UrlUtils()
        const egdApiClient = EgdApiClient({
          host: `${appConfig.GATEWAY_URL}/v2`,
          commonHeaders: {
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined
          },
          urlUtils
        })
        const availableAuthPolicies = await egdApiClient.authService.getAvailableAuthPolicies()
        const auth = Auth(availableAuthPolicies as IAuthPolicy[])
        const settingsForm = QuarantineNotificationForm({ egdApiClient, accountId, domainId, auth })
        if (!canSetState.current) {
          return
        }
        if (!settingsForm.form.canRead) {
          setUiState({
            state: 'error',
            error: 'unauthorized'
          })
          return
        }
        setUiState({
          state: 'ready',
          props: {
            settingsForm,
            accountId,
            domainId,
            egdApiClient
          }
        })
      } catch {
        if (!canSetState.current) {
          return
        }
        setUiState({
          state: 'error',
          error: 'failed_to_load_dependencies'
        })
      }
    })()
  }, [accessToken, accessTokenObject])

  // TODO: when switching the UI to BDS v2 improve/standardize the UI state loading phases
  return useMemo(() => {
    switch (uiState.state) {
      case 'ready':
        return <QuarantineNotificationPage {...uiState.props} />
      case 'error':
        return (
          <SettingsPageContainer isContentLoading={false} isFailedToLoad={false}>
            <PageTitle title={formatMessage('title')} />
            <FormSpacer />
            <Alert severity="error" style={{ width: '100%' }}>
              {t(`page_loader_errors.${uiState.error}`)}
            </Alert>
          </SettingsPageContainer>
        )
      case 'pending':
      default:
        return (
          <SettingsPageContainer isContentLoading={false} isFailedToLoad={false}>
            <PageTitle title={formatMessage('title')} />
            <FormSpacer />
            <LinearProgress style={{ width: '100%' }} />
          </SettingsPageContainer>
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState.state])
}
